'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Trash2, Package, Layers } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import type { BoxType } from '../types';

interface BoxTypeCardProps {
  boxType: BoxType;
  onEdit: (item: BoxType) => void;
  onDelete: (item: BoxType) => void;
}

export function BoxTypeCard({ boxType, onEdit, onDelete }: BoxTypeCardProps) {
  const t = useTranslations('boxTypes');

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        <div className='mb-4 flex items-start justify-between'>
          <h3 className='truncate font-semibold'>{boxType.name}</h3>
          <Badge
            variant={boxType.status === 'active' ? 'default' : 'secondary'}
            className={cn(
              'shrink-0 text-xs',
              boxType.status === 'active'
                ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
                : 'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200'
            )}
          >
            {boxType.status === 'active'
              ? t('statusActive')
              : t('statusInactive')}
          </Badge>
        </div>

        <div className='text-muted-foreground mb-4 space-y-1.5 text-sm'>
          <p className='font-mono'>{boxType.code}</p>
          <p className='line-clamp-2'>{boxType.description ?? '—'}</p>
          <div className='flex items-center gap-1.5'>
            <Layers className='h-3.5 w-3.5' />
            <span>
              {t('maximumCapacity')}:{' '}
              <span className='text-foreground font-medium'>
                {boxType.max_capacity}
              </span>
            </span>
          </div>
          <div className='flex items-center gap-1.5'>
            <Package className='h-3.5 w-3.5' />
            <span>{t('boxesCount', { count: boxType.boxes_count ?? 0 })}</span>
          </div>
        </div>

        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <PermissionGate permissions={['box_types.edit']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onEdit(boxType)}
            >
              <Pencil className='h-4 w-4' />
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['box_types.delete']}>
            <Button
              variant='ghost'
              size='icon'
              className='text-destructive h-8 w-8'
              onClick={() => onDelete(boxType)}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </PermissionGate>
        </div>
      </CardContent>
    </Card>
  );
}
